import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { adminIntegrationsRepository, type IntegrationCompany } from '@web/data/repositories';
import { Card, EmptyHint, Input, useDebouncedValue, useOrgContext } from '@web/ui/core';
import { twcn } from '@web/ui/core/utils';
import { t } from '@web/i18n';

import { CompanyDetail } from './company_detail';
import { activeTool, ChatStatusChip, chipStateOf, companiesKey, TOOL } from './integrations_shared';

const MIN_QUERY = 2;

function CompanyItem({
  company,
  active,
  onSelect,
}: Readonly<{
  company: IntegrationCompany;
  active: boolean;
  onSelect: () => void;
}>): React.JSX.Element {
  const tool = activeTool(company);
  return (
    <li>
      <button
        type="button"
        aria-current={active}
        onClick={onSelect}
        className={twcn(
          'flex w-full flex-col gap-1 border-t border-border-soft px-3.5 py-2.5 text-left',
          active ? 'bg-surface shadow-pill-active' : 'hover:bg-muted',
        )}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="truncate text-base font-medium text-foreground">{company.name}</span>
          <span className="flex shrink-0 items-center gap-1.5">
            {tool ? (
              <span className="text-meta text-muted-foreground">{TOOL[tool].name()}</span>
            ) : null}
            <ChatStatusChip state={tool ? chipStateOf(company[tool]) : 'none'} />
          </span>
        </span>
        <span className="truncate text-meta text-muted-foreground">
          {company.companies.map((c) => c.name).join(' · ')}
        </span>
      </button>
    </li>
  );
}

function ListBody({
  query,
  searching,
  selected,
  onSelect,
}: Readonly<{
  query: ReturnType<typeof useQuery<IntegrationCompany[]>>;
  searching: boolean;
  selected: string | null;
  onSelect: (id: string) => void;
}>): React.JSX.Element {
  if (query.isLoading) {
    return <EmptyHint density="compact">{t('integrations.list.loading')}</EmptyHint>;
  }
  if (query.isError) {
    return <EmptyHint density="compact">{t('integrations.list.error')}</EmptyHint>;
  }
  const companies = query.data ?? [];
  if (companies.length === 0) {
    return (
      <EmptyHint density="compact">
        {searching ? t('integrations.list.noMatch') : t('integrations.list.noneConnected')}
      </EmptyHint>
    );
  }
  return (
    <ul className="m-0 list-none p-0" aria-label={t('integrations.list.label')}>
      {companies.map((company) => (
        <CompanyItem
          key={company.id}
          company={company}
          active={company.id === selected}
          onSelect={() => {
            onSelect(company.id);
          }}
        />
      ))}
    </ul>
  );
}

export function CompaniesTab({
  selected,
  onSelect,
}: Readonly<{ selected: string | null; onSelect: (id: string) => void }>): React.JSX.Element {
  const [q, setQ] = useState('');
  const debounced = useDebouncedValue(q.trim(), 300);
  const term = debounced.length >= MIN_QUERY ? debounced : '';
  const { orgId } = useOrgContext();
  const query = useQuery({
    queryKey: companiesKey(term),
    queryFn: () => adminIntegrationsRepository.listCompanies(orgId, term || undefined),
  });
  const current = query.data?.find((c) => c.id === selected) ?? null;

  return (
    <Card>
      <div className="grid grid-cols-1 md:grid-cols-3">
        <aside className="flex min-w-0 flex-col border-b border-border-soft bg-paper md:border-b-0 md:border-r">
          <div className="p-3">
            <Input
              type="search"
              value={q}
              onChange={(event) => {
                setQ(event.target.value);
              }}
              placeholder={t('integrations.search.placeholder')}
              aria-label={t('integrations.search.label')}
            />
            {term ? null : (
              <p className="m-0 mt-2 text-meta text-muted-foreground">
                {t('integrations.search.hint')}
              </p>
            )}
          </div>
          <ListBody query={query} searching={term !== ''} selected={selected} onSelect={onSelect} />
        </aside>
        <section className="min-w-0 p-5 md:col-span-2">
          {current ? (
            <CompanyDetail key={current.id} company={current} />
          ) : (
            <EmptyHint>{t('integrations.detail.empty')}</EmptyHint>
          )}
        </section>
      </div>
    </Card>
  );
}
